'use client';

import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Select, Option, SelectProps } from 'src/components/PulseUI/Select';

interface ControlledJoySelectProps extends Omit<SelectProps, 'onChange'> {
  name: string;
  options: { value: string | number; label: string }[];
  rules?: Record<string, unknown>;
  errorText?: string;
  'data-testid'?: string;
}

const ControlledJoySelect = ({ ...props }: ControlledJoySelectProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  if (!props) return null;

  const errorMessage = props?.errorText || 'This field is required';
  const testId = props['data-testid'] || `joy-select-${props.name}`;
  const errorId = `${props.name}-error`;
  const hasError = !!errors[props.name];

  return (
    <Controller
      name={props.name}
      control={control}
      rules={{
        required: props.required ? errorMessage : false,
        ...props.rules,
      }}
      render={({ field }) => {
        return (
          <div data-testid={testId}>
            <Select
              id={props.id || props.name}
              value={field.value || ''}
              placeholder={props.placeholder || 'Select a manager'}
              onChange={(_, newValue) => {
                field.onChange(newValue);
              }}
              disabled={props.disabled}
              error={hasError}
              className={`rounded-full ${props.className}`}
              aria-label={
                props.label || props.placeholder || 'Select an option'
              }
              aria-invalid={hasError}
              aria-describedby={hasError ? errorId : undefined}
              aria-required={props.required}
            >
              {props?.options?.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            {hasError && (
              <p
                id={errorId}
                role="alert"
                className="text-red-500 text-xs mt-1"
                data-testid={`${testId}-error`}
              >
                {errors[props.name]?.message as string}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};

export default ControlledJoySelect;
